import { useEffect, useMemo, useState } from "react";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { updateProfile, updateSelf } from "../../../../../api/UserAPI";
import { useProfileStore } from "../../../../../stores/UserStore";
import {
  Button,
  Checkbox,
  Divider,
  Group,
  LoadingOverlay,
  NumberInput,
  Stack,
  Switch,
  Text,
} from "@mantine/core";
import BackButton from "../../../../../components/View/BackButton";
import { notifications } from "@mantine/notifications";
import LeadAnswerPositiveNotif from "../../controller/LeadAnswerPositiveNotif";
import LeadAnswerNegativeNotif from "../../controller/LeadAnswerNegativeNotif";
import LeadAnswerNeutralNotif from "../../controller/LeadAnswerNeutralNotif";
import NotificationDigest from "./NotificationDigest";
import { TimeInput } from "@mantine/dates";
import { IconClock } from "@tabler/icons-react";
import { getColumnsWatchlist, updateColumnsWatchlist } from "../../../../../api/PRMAPI";

const NotificationContent = ({ handleClose }) => {
  const lang = useLanguage((s) => s.language);
  const [loading, setLoading] = useState(true);
  const profile = useProfileStore((s) => s.profile);
  const [submitting, setSubmitting] = useState(false);
  const [watched, setWatched] = useState([]);
  const [enableWatch, setEnableWatch] = useState(false);
  const [frequency, setFrequency] = useState("instant");
  const [dailyTime, setDailyTime] = useState(profile?.send_prm_column_entry_mail_digest_time || "09:00:00");

  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const data = Object.fromEntries(fd);

    data.send_prm_column_entry_mail = enableWatch ? true : false;
    data.send_prm_column_entry_mail_frequency = frequency;
    data.send_prm_column_entry_mail_digest_time = dailyTime;

    let watchedColumns = watched.map(item => {
      if (item.status) {
        return `status:${item.status}`;
      }
      else if (item.custom_status) {
        return `custom:${item.custom_status}`;
      }
    });

    setSubmitting(true);
    updateProfile(null, data)
      .then((response) => {
        updateColumnsWatchlist({ watched_columns: watchedColumns })
          .then((r) => {
            handleClose();
          })
          .finally(() => setSubmitting(false));

        updateSelf();
        notifications.show({
          message: lang.resellers.options.saved,
          color: "green.8",
        });
      })
      .finally(() => setSubmitting(false));
  };

  useEffect(() => {
    if (loading) {
      updateSelf().then((response) => {
        setDailyTime(response.data.user_profile?.send_prm_column_entry_mail_digest_time || "09:00:00");
        setFrequency(response.data.user_profile?.send_prm_column_entry_mail_frequency || "instant");
        setEnableWatch(response.data.user_profile?.send_prm_column_entry_mail);

        getColumnsWatchlist().then((response) => {
          setWatched(response.data.watched_columns?.map((item, index) => {
            if (item.startsWith("status:")) {
              return { status: item.replace("status:", ""), id: index + 1 };
            }
            else if (item.startsWith("custom:")) {
              return { custom_status: item.replace("custom:", ""), id: index + 1 };
            }
          }) || []);
        }).finally(() => {
          setLoading(false);
        });
      })
    }
  }, [loading]);

  return (
    <>
      <Stack
        pos={"relative"}
        gap={"xs"}
        h={"100%"}
        component="form"
        onSubmit={handleSubmit}
        id="form"
      >
        <LoadingOverlay visible={loading} />

        <Text>{lang.prm.notifications.top_text}</Text>

        <Stack>
          <LeadAnswerPositiveNotif />

          <LeadAnswerNegativeNotif />

          <LeadAnswerNeutralNotif />
        </Stack>


        <Divider my={"md"} />

        <Switch label={lang.prm.notifications.enable_watch} name="send_prm_column_entry_mail" size="md" checked={enableWatch} onChange={(e) => setEnableWatch(e.target.checked)} />

        <NotificationDigest watched={watched} setWatched={setWatched} disabled={!enableWatch} />

        <Group mt={"md"} gap={"xl"}>
          <Checkbox
            label={lang.workflows.nodes.events.delay.immediate}
            checked={frequency === "instant"}
            name=""
            size="md"
            disabled={!enableWatch}
            onChange={(e) => setFrequency(e.target.checked ? "instant" : "daily_digest")}
          />
          <Checkbox
            label={lang.sync_zapier.frequency.daily}
            checked={frequency === "daily_digest"}
            size="md"
            disabled={!enableWatch}
            onChange={(e) => setFrequency(e.target.checked ? "daily_digest" : "instant")}
          />
        </Group>

        {
          frequency === "daily_digest" ? (
            <Group align='center'>
              <Text c="dimmed">
                {lang.prm.notifications.recap}
              </Text>

              <TimeInput
                name="send_prm_column_entry_mail_digest_time"
                required
                value={dailyTime}
                onChange={e => setDailyTime(e.target.value)}
                leftSection={<IconClock size={18} />}
                right
                w={130}
                size="sm"
                withSeconds
                disabled={submitting || !enableWatch}
              />
            </Group>
          )
            :
            <Text c="dimmed" mt={7}>
              {lang.prm.notifications.immediate_text}
            </Text>
        }
      </Stack>

      <Group justify="end" mt={"xl"}>
        <BackButton onClick={handleClose} size="md" />
        <Button loading={submitting} form="form" type="submit" size="md">
          {lang.global.save}
        </Button>
      </Group>
    </>
  );
};

export default NotificationContent;
